import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from '../layout/Header';
import { Footer } from './Footer';
import logo from '../../assets/img/Logo.png';
import SidebarNav from './SidebarNav';
import SidebarHelp from './SidebarHelp';
import MobileMenuOverlay from './MobileMenuOverlay';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import OverageNotification from '../license-buy/OverageNotification';
import UnpaidInvoiceBanner from '../license-buy/UnpaidInvoiceBanner';
import ContentMasterSubscriptionInsufficientModal from '../content-master/ContentMasterSubscriptionInsufficientModal';
import { useAppDispatch } from '../../hooks/redux';
import { fetchContentMasterMenuCounts } from '../../store/slices/contentMasterSlice';
import { pickDefaultContentMasterChannelId } from '../../utils/contentMasterWorkspace';
import { helper } from '../../utils/helper';
import { isDataSourceConfigured } from '../../utils/dataSourceHelpers';
import { usePermissions } from '../../hooks/usePermissions';

interface DashboardLayoutProps {}

const CONTENT_MASTER_ROUTE_PREFIX = '/features/content-master';

const DashboardLayout: React.FC<DashboardLayoutProps> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const loading = useSelector((state: RootState) => state.loading.isLoading);
  const profile = useSelector((state: RootState) => state.profile.contact);
  const subscriptions = useSelector((state: RootState) => state.licenses.items);
  const dataSource = useSelector((state: RootState) => state.dataSource.data);
  const isDataInitialized = useSelector(
    (state: RootState) => state.auth.isDataInitialized
  );
  const workspaceConfig = useSelector(
    (state: RootState) => state.contentMaster.workspaceConfig
  );
  const filtersSource = useSelector((state: RootState) => state.contentMaster.filters.source);
  const bulkReviewSource = useSelector(
    (state: RootState) => state.contentMaster.bulkReview.filters.source
  );

  const isSubuser = Boolean(profile?.parentUserId);
  const { can } = usePermissions();
  const hasActiveContentMaster = subscriptions.some(
    (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
  );
  const dataSourceReady = isDataSourceConfigured(dataSource);
  const sidebarReady =
    isDataInitialized &&
    hasActiveContentMaster &&
    can('content_master_read') &&
    dataSourceReady;

  const isContentMasterRoute = location.pathname.startsWith(CONTENT_MASTER_ROUTE_PREFIX);

  const handleToggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((open) => !open);
  }, []);

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarReady || !workspaceConfig) {
      return;
    }
    const fromFilters = filtersSource.trim();
    const fromBulkReview = bulkReviewSource.trim();
    const fallback =
      pickDefaultContentMasterChannelId(workspaceConfig.platforms) ?? '';
    const resolvedSource = fromFilters || fromBulkReview || fallback;
    if (!resolvedSource) {
      return;
    }
    const dataSourceId = workspaceConfig.dataSourceId ?? null;
    const run = () => {
      dispatch(
        fetchContentMasterMenuCounts({
          source: resolvedSource,
          dataSourceId,
        })
      );
    };
    run();
    if (!isContentMasterRoute) {
      return;
    }
    const intervalId = window.setInterval(run, 5000);
    return () => window.clearInterval(intervalId);
  }, [
    dispatch,
    sidebarReady,
    workspaceConfig,
    isContentMasterRoute,
    filtersSource,
    bulkReviewSource,
  ]);

  return (
    <div className="dashboard-layout">
      <aside className="sidebar" aria-hidden={isMobileMenuOpen}>
        <div className="sidebar-header">
          <img src={logo} alt="Seller2Go" className="logo" />
        </div>
        <div className="sidebar-body">
          <nav className="sidebar-nav" aria-label={t('navigation')}>
            <SidebarNav isSubuser={isSubuser} />
          </nav>
        </div>
        <SidebarHelp />
      </aside>

      <main className="main-content">
        <UnpaidInvoiceBanner />
        <Header
          isLoginHeader={false}
          showLoginHeader={false}
          onMenuToggle={handleToggleMobileMenu}
          isMobileMenuOpen={isMobileMenuOpen}
          onProfileClick={handleCloseMobileMenu}
        />
        <MobileMenuOverlay
          isOpen={isMobileMenuOpen}
          isSubuser={isSubuser}
          onClose={handleCloseMobileMenu}
        />
        <OverageNotification />
        <div className={`content ${loading ? 'loading-progress' : ''}`}>
          <div className="loading-spinner-wrapper">
            <div className="loading-spinner" aria-label={t('loading_text')}></div>
          </div>
          <Outlet />
        </div>
        <Footer />
      </main>
      <ContentMasterSubscriptionInsufficientModal />
    </div>
  );
};

export default DashboardLayout;
